import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import * as trialRejectedDevicesService from '../services/trialRejectedDevices';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,

  fetchingError: null,
  creatingUpdatingError: null,
};

const sliceName = 'trialRejectedDevices';

export const fetchAllTrialRejectedDevices = createAsyncThunk(
  `${sliceName}/fetchAllTrialRejectedDevices`,
  (args, thunkAPI) => {
    return trialRejectedDevicesService
      .getTrialRejectedDevices()
      .catch((error) => {
        error.clientMessage = "Can't get trial rejected devices from server";
        return thunkAPI.rejectWithValue(error);
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return trialRejectedDevicesService.getTrialRejectedDeviceById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get trial rejected device from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);


export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return trialRejectedDevicesService.addTrialRejectedDevice(args.deviceId)
      .catch((error) => {
        error.clientMessage = "Can't post trial rejected device to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return trialRejectedDevicesService.editTrialRejectedDevice(args.id, args.isOn)
      .catch((error) => {
        error.clientMessage = "Can't update trial rejected device on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

const trialRejectedDevices = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAllTrialRejectedDevices.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchAllTrialRejectedDevices.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchAllTrialRejectedDevices.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },

    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
  },
});

const { reducer } = trialRejectedDevices;

const trialRejectedDevicesAdapterSelectors = entitiesAdapter.getSelectors(
  (state) => state[sliceName]
);

const selectedTrialRejectedDevicesSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => trialRejectedDevicesAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const trialRejectedDevicesSelectors = {
  ...trialRejectedDevicesAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,

  state: (state) => state[sliceName],
  selectedTrialRejectedDevices: selectedTrialRejectedDevicesSelector,
};

export default reducer;


// WEBPACK FOOTER //
// ./src/store/trialRejectedDevicesSlice.js